/**
 * Core interface for user data that persists locally
 * All data is organized by organization ID
 */
export interface UserData {
    [orgId: string]: OrgUserData;
}

/**
 * User data specific to an organization
 */
export interface OrgUserData {
    bookmarks: string[]; // Array of video IDs
    lastViewed: LastViewedVideo[];
    preferences: UserPreferences;
}

/**
 * Last viewed video information
 */
export interface LastViewedVideo {
    videoId: string;
    viewedAt: string; // ISO timestamp
}

/**
 * User preferences and settings (empty for now, but ready for future expansion)
 */
export interface UserPreferences {
    // Empty object for now - can be expanded later
}

/**
 * Default organization user data structure
 */
export const defaultOrgUserData: OrgUserData = {
    bookmarks: [],
    lastViewed: [],
    preferences: {},
};

/**
 * Default user data structure (empty object)
 */
export const defaultUserData: UserData = {};

/**
 * Type for organization user data keys
 */
export type OrgUserDataKey = keyof OrgUserData;

/**
 * Helper type to get the type of a specific org user data field
 */
export type OrgUserDataValue<K extends OrgUserDataKey> = OrgUserData[K];

/**
 * Helper function to get org data with defaults
 */
export const getOrgUserData = (userData: UserData, orgId: string): OrgUserData => {
    return userData[orgId] || defaultOrgUserData;
};
